import { useEffect, useState } from 'react';
import { aiApi } from '../../../api';
import Select from '../../../components/common/Select';

const MASK = '••••••••';

const PROVIDER_INFO: Record<string, { label: string; keyGuideUrl: string; keyHint: string }> = {
  anthropic: { label: 'Anthropic (Claude)',    keyGuideUrl: 'https://console.anthropic.com/settings/keys', keyHint: 'sk-ant-…' },
  openai:    { label: 'OpenAI (GPT)',          keyGuideUrl: 'https://platform.openai.com/api-keys',         keyHint: 'sk-…'     },
  google:    { label: 'Google (Gemini)',       keyGuideUrl: 'https://aistudio.google.com/app/apikey',       keyHint: 'AIza…'    },
};

export default function SettingsAI() {
  const [configured, setConfigured] = useState(false);
  const [provider, setProvider] = useState('anthropic');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [models, setModels] = useState<Record<string, { value: string; label: string }[]>>({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    aiApi.models().then((r) => setModels(r.models)).catch(() => null);
    aiApi.settings().then((r) => {
      if (r.configured && r.settings) {
        setConfigured(true);
        setProvider(r.settings.provider);
        setModel(r.settings.model);
        setApiKey(MASK);
        setEnabled(r.settings.enabled);
      }
    }).catch(() => null);
  }, []);

  const onProviderChange = (p: string) => {
    setProvider(p);
    const first = models[p]?.[0]?.value;
    if (first) setModel(first);
    setApiKey('');
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await aiApi.saveSettings({
        provider, model, enabled,
        api_key: apiKey === MASK ? undefined : apiKey,
      });
      setConfigured(true);
      setMessage({ kind: 'ok', text: 'Saved.' });
      setApiKey(MASK);
    } catch (err) {
      setMessage({ kind: 'err', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await aiApi.testSettings({
        provider, model,
        api_key: apiKey === MASK ? undefined : apiKey,
      });
      setMessage({ kind: 'ok', text: 'Connection OK — provider replied.' });
    } catch (err) {
      setMessage({ kind: 'err', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const info = PROVIDER_INFO[provider];

  return (
    <div style={{ maxWidth: 640 }} className="stack">
      <h1>AI provider</h1>
      <p className="muted">
        Litigate uses your own AI provider account — bring your own API key. No provider lock-in. Keys are encrypted at rest.
      </p>

      <div className="card stack">
        <div>
          <label>Provider</label>
          <Select
            value={provider}
            onChange={onProviderChange}
            options={Object.keys(PROVIDER_INFO).map((p) => ({ value: p, label: PROVIDER_INFO[p].label }))}
            searchPlaceholder="Search providers…"
          />
        </div>

        <div>
          <label>Model</label>
          <Select
            value={model}
            onChange={setModel}
            options={(models[provider] || []).map((m) => ({ value: m.value, label: m.label, hint: m.value }))}
            searchPlaceholder="Search models…"
          />
        </div>

        <div>
          <label>API key</label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={info?.keyHint}
            onFocus={() => { if (apiKey === MASK) setApiKey(''); }}
          />
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Get a key from <a href={info?.keyGuideUrl} target="_blank" rel="noreferrer">{info?.keyGuideUrl}</a>.
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ width: 'auto' }} />
          <span>Enable AI features</span>
        </label>

        {message && (
          <div style={{ color: message.kind === 'ok' ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {message.text}
          </div>
        )}

        <div className="row">
          <button className="primary" onClick={save} disabled={busy}>{configured ? 'Save changes' : 'Save'}</button>
          <button onClick={test} disabled={busy || !apiKey}>Test connection</button>
        </div>
      </div>

      <p className="muted" style={{ fontSize: 13 }}>
        Chat + precedent library + drafting assistant land in Phase 3. This screen is functional now so you can onboard your key.
      </p>
    </div>
  );
}
